/**
 * Workspace route manifest.
 *
 * IMPORTANT: When you add a new route file to `apps/web/src/routes/`,
 * you must also add it here so it can be rendered inside workspace tabs.
 *
 * Exact routes are matched first, then pattern routes are checked in order.
 * Pattern routes use a simple prefix/suffix test — order matters.
 */
import type React from "react";
import { DashboardPage } from "@/routes/specPilot.index";
import { ComparisonsPage } from "@/routes/comparisons.index";
import { NewWorkspaceTabPage } from "@/routes/workspaces.new";
import { NewSubmittalPage } from "@/routes/specPilot.new";
import { RevPilotPage } from "@/routes/revPilot.index";
import { NewRevPilotPage } from "@/routes/revPilot.new";
import { TakeoffPilotPage } from "@/routes/takeoffPilot.index";
import { NewTakeoffPilotPage } from "@/routes/takeoffPilot.new";
import { RevCollabPage } from "@/routes/revCollab";
import { ProcurePilotPage } from "@/routes/procurePilot.index";
import { NewProcurePilotPage } from "@/routes/procurePilot.new";
import { ProcurePilotJobPage } from "@/routes/procurePilot.$jobId";
import { ProjectsIndexPage } from "@/routes/index";
import { ProjectRoute } from "@/routes/project";
import { ProjectDocumentDetailPage } from "@/routes/project.documents.$documentId";
import { RevCollabDocumentPage } from "@/routes/revCollab.documents.$documentId";
import { RevCollabRevisionPage } from "@/routes/revCollab.revisions.$revisionId";
import { InboxPage } from "@/routes/inbox";
import { JobDetailPage } from "@/routes/specPilot.$jobId";
import { ComparisonDetailPage } from "@/routes/revPilot.$comparisonId";
import { SubmittalViewerPage } from "@/routes/specPilot.$jobId_.submittal";
import { TakeoffPilotJobPage } from "@/routes/takeoffPilot.$jobId";
import { TrainingPage } from "@/routes/training";
import { AdminTrainingPage } from "@/routes/admin.training";

/** Exact route string → component */
const exactRoutes: Record<string, React.ComponentType> = {
  "/": ProjectsIndexPage,
  "/specPilot": DashboardPage,
  "/comparisons": ComparisonsPage,
  "/workspaces/new": NewWorkspaceTabPage,
  "/specPilot/new": NewSubmittalPage,
  "/revPilot": RevPilotPage,
  "/revPilot/new": NewRevPilotPage,
  "/takeoffPilot": TakeoffPilotPage,
  "/takeoffPilot/new": NewTakeoffPilotPage,
  "/revCollab": RevCollabPage,
  "/procurePilot": ProcurePilotPage,
  "/procurePilot/new": NewProcurePilotPage,
  "/inbox": InboxPage,
  "/project": ProjectRoute,
  "/training": TrainingPage,
  "/admin/training": AdminTrainingPage,
};

/** Pattern-matched routes, checked in order. */
const patternRoutes: Array<{
  test: (route: string) => boolean;
  component: React.ComponentType;
}> = [
  // /specPilot/:id/submittal — must be before generic /specPilot/ match
  {
    test: (r) => r.startsWith("/specPilot/") && r.endsWith("/submittal"),
    component: SubmittalViewerPage,
  },
  // /specPilot/:id
  { test: (r) => r.startsWith("/specPilot/"), component: JobDetailPage },
  // /revPilot/:id
  { test: (r) => r.startsWith("/revPilot/"), component: ComparisonDetailPage },
  // /takeoffPilot/:id
  { test: (r) => r.startsWith("/takeoffPilot/"), component: TakeoffPilotJobPage },
  // /procurePilot/:id
  { test: (r) => r.startsWith("/procurePilot/"), component: ProcurePilotJobPage },
  // /revCollab/revisions/:revisionId
  {
    test: (r) => r.startsWith("/revCollab/revisions/"),
    component: RevCollabRevisionPage,
  },
  // /revCollab/documents/:documentId
  {
    test: (r) => r.startsWith("/revCollab/documents/"),
    component: RevCollabDocumentPage,
  },
  // /project/documents/:documentId
  {
    test: (r) => r.startsWith("/project/documents/"),
    component: ProjectDocumentDetailPage,
  },
];

/**
 * Resolve a workspace route string to its page component.
 * Returns `ProjectsPage` as a fallback.
 */
export function resolveRouteComponent(route: string): React.ComponentType {
  if (exactRoutes[route]) {
    return exactRoutes[route];
  }

  for (const { test, component } of patternRoutes) {
    if (test(route)) {
      return component;
    }
  }

  return ProjectsIndexPage;
}
